import { Injectable } from '@angular/core';
import { Action, Selector, State, StateToken, type StateContext } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import { Undoable } from '@medsurf/flat-decorators';
import { HistoryEntityBaseState } from '../base/history-entity.base.state';

export const CHOICE_ENTITY_TOKEN = new StateToken<ChoiceEntityStateModel>(FlatModels.EntityControlModels.EntityQueryType.enum.CHOICE_ENTITY);

export type ChoiceEntityStateModel = FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateModel<FlatModels.ChoiceEntityModels.Choice>;

@State<ChoiceEntityStateModel>({
  name: CHOICE_ENTITY_TOKEN,
  defaults: {
    snapshots: {},
    entities: {},
  }
})
@Injectable()
export class ChoiceEntityState extends HistoryEntityBaseState<FlatModels.ChoiceEntityModels.Choice, ChoiceEntityStateModel> {
  //<editor-fold desc="Selectors">

  @Selector([ChoiceEntityState])
  static override typedEntities$(state: ChoiceEntityStateModel): { [id: string]: FlatModels.ChoiceEntityModels.Choice } {
    return HistoryEntityBaseState.typedEntities$(state);
  }

  @Selector([ChoiceEntityState.typedEntities$])
  static override entities$(typedEntities: { [id: string]: FlatModels.ChoiceEntityModels.Choice }): FlatModels.ChoiceEntityModels.Choice[] {
    return HistoryEntityBaseState.entities$(typedEntities);
  }

  @Selector([ChoiceEntityState.typedEntities$])
  static override entity$(typedEntities: { [id: string]: FlatModels.ChoiceEntityModels.Choice }) {
    return HistoryEntityBaseState.entity$(typedEntities);
  }

  @Selector([ChoiceEntityState.typedEntities$])
  static override entitiesByIds$(typedEntities: { [id: string]: FlatModels.ChoiceEntityModels.Choice }) {
    return HistoryEntityBaseState.entitiesByIds$(typedEntities);
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  //<editor-fold desc="Insertion">

  @Action(FlatActions.ChoiceEntityActions.GetEntitySuccess)
  override getEntitySuccess(state: StateContext<ChoiceEntityStateModel>,
                            action: FlatActions.ChoiceEntityActions.GetEntitySuccess) {
    return super.getEntitySuccess(state, action);
  }

  @Action(FlatActions.ChoiceEntityActions.GetEntitiesSuccess)
  override getEntitiesSuccess(state: StateContext<ChoiceEntityStateModel>,
                              action: FlatActions.ChoiceEntityActions.GetEntitiesSuccess) {
    return super.getEntitiesSuccess(state, action);
  }

  @Action(FlatActions.ChoiceEntityActions.ModifyEntitySuccess)
  override modifyEntitySuccess(state: StateContext<ChoiceEntityStateModel>,
                               action: FlatActions.ChoiceEntityActions.ModifyEntitySuccess) {
    return super.modifyEntitySuccess(state, action);
  }

  @Action(FlatActions.ChoiceEntityActions.ModifyEntitiesSuccess)
  override modifyEntitiesSuccess(state: StateContext<ChoiceEntityStateModel>,
                                 action: FlatActions.ChoiceEntityActions.ModifyEntitiesSuccess) {
    return super.modifyEntitiesSuccess(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Edit">

  @Action(FlatActions.ChoiceEntityActions.AddEntity)
  @Undoable(
    FlatActions.ChoiceEntityActions.AddEntity,
    CHOICE_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD
  )
  override addEntity(state: StateContext<ChoiceEntityStateModel>,
                     action: FlatActions.ChoiceEntityActions.AddEntity) {
    return super.addEntity(state, action);
  }

  @Action(FlatActions.ChoiceEntityActions.UpdateEntity)
  @Undoable(
    FlatActions.ChoiceEntityActions.UpdateEntity,
    CHOICE_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE
  )
  override updateEntity(state: StateContext<ChoiceEntityStateModel>,
                        action: FlatActions.ChoiceEntityActions.UpdateEntity) {
    return super.updateEntity(state, action);
  }

  @Action(FlatActions.ChoiceEntityActions.RemoveEntity)
  @Undoable(
    FlatActions.ChoiceEntityActions.RemoveEntity,
    CHOICE_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE
  )
  override removeEntity(state: StateContext<ChoiceEntityStateModel>,
                        action: FlatActions.ChoiceEntityActions.RemoveEntity) {
    return super.removeEntity(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Snapshot">

  @Action(FlatActions.ChoiceEntityActions.UndoSnapshot)
  override undoSnapshot(state: StateContext<ChoiceEntityStateModel>,
                        action: FlatActions.ChoiceEntityActions.UndoSnapshot) {
    return super.undoSnapshot(state, action);
  }

  @Action(FlatActions.ChoiceEntityActions.RedoSnapshot)
  override redoSnapshot(state: StateContext<ChoiceEntityStateModel>,
                        action: FlatActions.ChoiceEntityActions.RedoSnapshot) {
    return super.redoSnapshot(state, action);
  }

  @Action(FlatActions.ChoiceEntityActions.CleanUpSnapshots)
  override cleanUpSnapshots(state: StateContext<ChoiceEntityStateModel>,
                            action: FlatActions.ChoiceEntityActions.CleanUpSnapshots) {
    return super.cleanUpSnapshots(state, action);
  }

  //</editor-fold>

  @Action(FlatActions.ChoiceEntityActions.RearrangeChoices)
  rearrangeChoices({getState, dispatch}: StateContext<ChoiceEntityStateModel>,
                   {question, choices}: FlatActions.ChoiceEntityActions.RearrangeChoices) {
    // Get Entities
    const entities = structuredClone(getState().entities);
    
    // Check Question Type
    if (question.type === FlatModels.QuestionEntityModels.QuestionType.enum.KprimeQuestion || question.type === FlatModels.QuestionEntityModels.QuestionType.enum.TrueFalseQuestion) {
      // Get "before" choices sorted
      const choicesSorted = question.choices.map(choice => entities[choice]).sort((a, b) => a.order - b.order).map(choice => choice.id);
      // Get "before" choices map (id -> solution)
      const choicesMap = new Map(choicesSorted.map((choice, index) => ([choice, question.solutions[index]] )));

      // Get "after" choices and solutions
      const newChoices = choices.map(choice => typeof choice === 'string' ? choice : choice.id);
      const newSolutions = newChoices.map(choiceId => choicesMap.get(choiceId) ?? '0');

      // Update choices
      newChoices.forEach((choiceId, index) => {
        const choice = entities[choiceId];
        if (choice) {
          const choiceClone = structuredClone(choice);
          choiceClone.order = index;
          dispatch(new FlatActions.ChoiceEntityActions.UpdateEntity(choiceClone));
        }
      })

      // Update question
      const questionClone = structuredClone(question);
      questionClone.solutions = newSolutions;
      dispatch(new FlatActions.QuestionEntityActions.UpdateEntity(questionClone));

    } else if (question.type === FlatModels.QuestionEntityModels.QuestionType.enum.MappingQuestion) {
      // Get "before" choices sorted
      const choicesSorted = question.choices.map(choice => entities[choice]).sort((a, b) => a.order - b.order).map(choice => choice.id);
      // Get "before" solution ids
      const solutionIds = question.solutions.map(index => choicesSorted[Number(index)]);
      
      // Get "after" choices
      const newChoices = choices.map(choice => typeof choice === 'string' ? choice : choice.id);
      // Get "after" choices map (id -> index)
      const newChoicesMap = new Map(newChoices.map((choiceId, index) => ([choiceId, `${index}`]) ));
      // Get "after" solutions  
      const newSolutions = solutionIds.map(id => newChoicesMap.get(id)).filter(i => i !== undefined);

      // Update choices
      newChoices.forEach((choiceId, index) => {
        const choice = entities[choiceId];
        if (choice) {
          const choiceClone = structuredClone(choice);
          choiceClone.order = index;
          dispatch(new FlatActions.ChoiceEntityActions.UpdateEntity(choiceClone));
        }
      })

      // Update question
      const questionClone = structuredClone(question);
      questionClone.solutions = newSolutions;
      dispatch(new FlatActions.QuestionEntityActions.UpdateEntity(questionClone));
    } else if (question.type === FlatModels.QuestionEntityModels.QuestionType.enum.LongListQuestion) {
      // Get "before" choices sorted
      const choicesSorted = question.choices.map(choice => entities[choice]).sort((a, b) => a.order - b.order).map(choice => choice.id);
      // Get "before" solution index
      const solutionId = question.solution !== null ? choicesSorted[question.solution]: null;
      
      // Get "after" choices and solution
      const newChoices = choices.map(choice => typeof choice === 'string' ? choice : choice.id);
      const newSolution = solutionId ? newChoices.indexOf(solutionId) : null;

      // Update choices
      newChoices.forEach((choiceId, index) => {
        const choice = entities[choiceId];
        if (choice) {
          const choiceClone = structuredClone(choice);
          choiceClone.order = index;
          dispatch(new FlatActions.ChoiceEntityActions.UpdateEntity(choiceClone));
        }
      })

      // Update question
      if (newSolution !== question.solution) {
        const questionClone = structuredClone(question);
        questionClone.solution = newSolution;
        dispatch(new FlatActions.QuestionEntityActions.UpdateEntity(questionClone));
      }
    }
  }

  //</editor-fold>
}
